/**
 * @fileoverview Componente Gatekeeper para protección con Google reCAPTCHA v2
 * @description Muestra un widget de reCAPTCHA antes de permitir el acceso a la aplicación
 */

import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { captchaService } from '../services/captchaService';
import './gatekeeper.css';

interface GatekeeperProps {
  onValidationSuccess: () => void;
}

/**
 * Componente Gatekeeper que protege el acceso a la aplicación con reCAPTCHA v2
 * @param props - Propiedades del componente
 * @param props.onValidationSuccess - Callback que se ejecuta cuando la validación es exitosa
 * @returns JSX.Element
 */
const Gatekeeper: React.FC<GatekeeperProps> = ({ onValidationSuccess }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  // Obtener la Site Key desde las variables de entorno
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    return (
      <div className="gatekeeper-container">
        <div className="gatekeeper-error">
          <h2>Error de configuración</h2>
          <p>Por favor, contacte al administrador.</p>
        </div>
      </div>
    );
  }

  /**
   * Maneja el cambio del reCAPTCHA cuando el usuario lo completa
   * @param token - Token generado por reCAPTCHA
   */
  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    setError(null); // Limpiar errores previos cuando se obtiene un nuevo token
  };

  /**
   * Maneja la expiración del token de reCAPTCHA
   */
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setError('La validación ha expirado. Por favor, complételo nuevamente.');
  };

  /**
   * Maneja errores del widget de reCAPTCHA
   */
  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setError('Error al cargar la validación. Por favor, recargue la página.');
  };

  /**
   * Valida el token de reCAPTCHA con el backend
   */
  const handleContinue = async () => {
    if (!captchaToken) {
      setError('Por favor, complete la validación antes de continuar.');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const response = await captchaService.validateCaptcha(captchaToken);

      if (response.success) {
        // El token se mantiene solo en memoria (state), no se guarda en localStorage
        onValidationSuccess();
      } else {
        setError(response.message || 'La validación falló. Por favor, inténtelo nuevamente.');
        // Resetear el reCAPTCHA para que el usuario pueda intentar de nuevo
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      }
    } catch (error: any) {  
      setError(error.message || 'Error con la validación. Por favor, inténtelo nuevamente.');
      // Resetear el reCAPTCHA para que el usuario pueda intentar de nuevo
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="gatekeeper-container">
      <div className="gatekeeper-content">
      <div className="gatekeeper-logo-container">
        <img src="/CG-Gestion-Logo-H.png" alt="Logo" />
      </div>
        <div className="gatekeeper-header">
          <h1>Verificación de Seguridad</h1>
          <p>Por favor, complete la validación para acceder</p>
        </div>

        <div className="gatekeeper-captcha">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={siteKey}
            onChange={handleCaptchaChange}
            onExpired={handleCaptchaExpired}
            onError={handleCaptchaError}
          />
        </div>

        {error && (
          <div className="gatekeeper-error-message">
            <span>{error}</span>
          </div>
        )}

        <div className="gatekeeper-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleContinue}
            disabled={!captchaToken || isValidating}
          >
            {isValidating ? 'Validando...' : 'Continuar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Gatekeeper;

